import React, { useEffect, useState } from "react";
import "./Login.css";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FiEye, FiEyeOff ,FiPhoneCall} from "react-icons/fi";
import {
  LuUser,
  LuLock,
  LuInfo,
  LuArrowRight,
  LuUserCog,
  LuFlaskConical,
  LuUsers,
  LuStethoscope,
  LuContact,
  LuFileText,
  LuShieldCheck,
  LuTestTube,
} from "react-icons/lu";
import Loader from "../../Components/GlobalLoader/Loader";
import DOMPurify from "dompurify";
import { useAuth } from "../../AuthProvider";
import LogoWithName from "../../assets/Gradient Heartbeat Logo.png";

const BRAND_FEATURES = [
  { label: "Accurate Results", Icon: LuTestTube },
  { label: "Trusted Care", Icon: LuShieldCheck },
  { label: "Better Lives", Icon: LuUsers },
];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoggingIn, user } = useAuth();
  const from = location.state?.from?.pathname || "/";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [role, setRole] = useState("Admin");

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const onSubmit = async () => {
    // Clear previous errors
    setUsernameError(null);
    setPasswordError(null);
    setLoginError(null);

    // Sanitize username only. Passwords are sent as-is, because sanitizing
    // can silently change characters like < or & and break valid logins.
    const sanitizedUsername = DOMPurify.sanitize(username.trim());
    // Username validation
    if (!sanitizedUsername) {
      setUsernameError("Please enter your username");
      return;
    }
    // Password validation
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }
    try {
      await login(sanitizedUsername, password);
      navigate(from, { replace: true });
    } catch (error) {
      setLoginError(
        error?.message ||
        "Invalid username or password. Please try again."
      );
    }
  };

  // Submit with Enter key
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !isLoggingIn) {
      onSubmit();
    }
  };

  return (
    <>
      <div className="login-shell">
        <div className="login-card">
          {/* ---------- LEFT: BRAND PANEL ---------- */}
          <div className="login-brand-panel">
            <div className="brand-main">
              <div className="brand-name">
                <img src={LogoWithName} alt="Cohezo Health" className="login-logo" />
                <h1>Cohezo Health <span className="brand-title">LIMS</span></h1>
              </div>
              <span className="brand-divider" aria-hidden="true" />
              <p className="brand-tagline">
                Better Diagnostics
                <br />
                for a Healthier Tomorrow
              </p>
            </div>

            <ul className="brand-features">
              {BRAND_FEATURES.map(({ label, Icon }) => (
                <li key={label} className="brand-feature">
                  <span className="brand-feature-icon">
                    <Icon size={26} aria-hidden="true" />
                  </span>
                  <span className="brand-feature-label">{label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ---------- RIGHT: LOGIN FORM ---------- */}
          <div className="login-form-panel">
            <div className="login-form-inner">
              <h1 className="login-title">Login</h1>
              <p className="login-subtitle">Access your account to continue</p>

              <div className="input-group">
                <label className="field-label" htmlFor="username">Username</label>
                <div className="input-wrapper">
                  <LuUser className="input-icon" size={18} aria-hidden="true" />
                  <input
                    id="username"
                    type="text"
                    value={username}
                    placeholder="Enter your username or email"
                    autoComplete="username"
                    disabled={isLoggingIn}
                    aria-invalid={!!usernameError}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setUsernameError(null);
                      setLoginError(null);
                    }}
                  />
                </div>
                {usernameError && (<p className="error-message">{usernameError}</p>)}
              </div>

              <div className="input-group">
                <label className="field-label" htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <LuLock className="input-icon" size={18} aria-hidden="true" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={isLoggingIn}
                    aria-invalid={!!passwordError}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError(null);
                      setLoginError(null);
                    }}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                    disabled={isLoggingIn}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (<FiEyeOff size={18} />) : (<FiEye size={18} />)}
                  </button>
                </div>
                {passwordError && (<p className="error-message">{passwordError}</p>)}
              </div>
              <div className="form-toolbar">
                <label className="remember-me">
                  <input type="checkbox" disabled={isLoggingIn} />
                  <span>Remember Me</span>
                </label>
                <Link to="/forgotpassword" className="forgot-link">Forgot Password?</Link>
              </div>

              <div className="error-container">
                {loginError && (<p className="error-message">{loginError}</p>)}
              </div>

              <div className="login_footer">
                <button
                  type="button"
                  className="login-button"
                  onClick={onSubmit}
                  disabled={isLoggingIn}
                >
                  {isLoggingIn ? (
                    "Please Wait..."
                  ) : (
                    <>
                      Login
                      <LuArrowRight size={22} aria-hidden="true" />
                    </>
                  )}
                </button>
              </div>

              <div className="help-footer">
                <div className="help-divider-row">
                  <div className="help-line" />

                  <span className="help-title">Need help?</span>

                  <div className="help-line" />
                </div>

                <button
                  type="button"
                  className="contact-admin-btn"
                  // onClick={onContactAdminPress}
                >
                  <FiPhoneCall
                    size={16}
                    strokeWidth={2}
                    className="contact-admin-icon"
                  />

                  <span>Contact your administrator</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
      {/* GLOBAL LOADER */}
      {isLoggingIn && (<Loader message="Please Wait..." />)}
    </>
  );
};

export default Login;